import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { replaceMultipleTokensFromAPI } from "../../assets/js/constants/commonFunctions";
import {
  resetPasswordSendOTPAPI,
  resetPasswordVerifyOTPAndResetPasswordAPI,
  getUserbyIdAPI,
  loginAPI,
} from "../../assets/js/constants/APIURLs";
import {
  FAILEDSTATUS,
  IDLESTATUS,
  LOADINGSTATUS,
  secureSecretkey,
  SUCCESSSTATUS,
} from "../../assets/js/constants/constant";

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, thunkAPI) => {
    try {
      const response = await axios.post(
        replaceMultipleTokensFromAPI(loginAPI),
        { email, password },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      const responseData = response?.data;
      const message = responseData?.message;
      const authJWTToken = responseData?.authJWTToken;
      const expiresAt = responseData?.expiresIn;
      const user = responseData?.user;
      const userId = user?.userId;
      const name = user?.name;
      const emailId = user?.email;
      const phoneNumber = user?.phoneNumber;
      const role = user?.role;
      const subscribedPlan = user?.subscribedPlan;
      const subscribedPlannedStartDate = user?.subscribedPlannedStartDate;
      const subscribedPlannedEndDate = user?.subscribedPlannedEndDate;
      const restaurantId = user?.restaurantId;
      const tokenId = user?.tokenId;
      return {
        message,
        authJWTToken,
        expiresAt,
        userId,
        name,
        emailId,
        phoneNumber,
        role,
        subscribedPlan,
        subscribedPlannedStartDate,
        subscribedPlannedEndDate,
        restaurantId,
        tokenId,
      };
    } catch (error) {
      toast.error(error.response?.data?.message);
      return thunkAPI.rejectWithValue(error.response?.data?.message);
    }
  },
);

export const resetPasswordSendOTP = createAsyncThunk(
  "auth/resetPasswordSendOTP",
  async ({ email, type }, thunkAPI) => {
    try {
      const response = await axios.post(
        replaceMultipleTokensFromAPI(resetPasswordSendOTPAPI),
        { email, type },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return response.data.message;
    } catch (error) {
      toast.error(error.response?.data?.message);
      return thunkAPI.rejectWithValue(error.response?.data?.message);
    }
  },
);

export const resetPasswordVerifyOTPAndResetPassword = createAsyncThunk(
  "auth/resetPasswordVerifyOTPAndResetPassword",
  async ({ email, otp, password }, thunkAPI) => {
    try {
      const response = await axios.post(
        replaceMultipleTokensFromAPI(resetPasswordVerifyOTPAndResetPasswordAPI),
        { email, otp, password },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      return response.data.message;
    } catch (error) {
      toast.error(error.response?.data?.message);
      return thunkAPI.rejectWithValue(error.response?.data?.message);
    }
  },
);

export const restoreAuth = createAsyncThunk(
  "auth/restoreAuth",
  async (_, thunkAPI) => {
    try {
      const expiresAt = Number(localStorage.getItem("expiresAt"));
      const userIdLocalStorage = localStorage.getItem("userId");
      if (!userIdLocalStorage || userIdLocalStorage.length < 10) {
        return toast.error("Invalid userId in storage");
      }
      const response = await axios.post(
        replaceMultipleTokensFromAPI(getUserbyIdAPI, { 1: userIdLocalStorage }),
        {
          loggedInUserId: userIdLocalStorage,
          expiresAt,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      const responseData = response?.data;
      const message = responseData?.message;
      const authJWTToken = responseData?.authJWTToken;
      const user = responseData?.user;
      const userId = user?.userId;
      const activated = user?.activated;
      const name = user?.name;
      const emailId = user?.email;
      const phoneNumber = user?.phoneNumber;
      const role = user?.role;
      const subscribedPlan = user?.subscribedPlan;
      const subscribedPlannedStartDate = user?.subscribedPlannedStartDate;
      const subscribedPlannedEndDate = user?.subscribedPlannedEndDate;
      const restaurantId = user?.restaurantId;
      const tokenId = user?.tokenId;
      return {
        message,
        authJWTToken,
        expiresAt,
        userId,
        activated,
        name,
        emailId,
        phoneNumber,
        role,
        subscribedPlan,
        subscribedPlannedStartDate,
        subscribedPlannedEndDate,
        restaurantId,
        tokenId,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue("Session restore failed");
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    isAuthenticated: false,
    isLoggingOut: false,
    authJWTToken: "",
    expiresAt: "",
    message: "",
    userId: "",
    activated: false,
    name: "",
    emailId: "",
    phoneNumber: "",
    role: "",
    subscribedPlan: "",
    subscribedPlannedStartDate: "",
    subscribedPlannedEndDate: "",
    restaurantId: "",
    tokenId: "",
    status: IDLESTATUS,
    error: null,
  },
  reducers: {
    logout(state) {
      state.isAuthenticated = false;
      state.isLoggingOut = true;
      state.authJWTToken = "";
      state.expiresAt = "";
      state.message = "";
      state.userId = "";
      state.activated = false;
      state.name = "";
      state.emailId = "";
      state.phoneNumber = "";
      state.role = "";
      state.subscribedPlan = "";
      state.subscribedPlannedStartDate = "";
      state.subscribedPlannedEndDate = "";
      state.restaurantId = "";
      state.tokenId = "";
      state.error = null;
      state.status = IDLESTATUS;
      localStorage.clear();
    },
    resetPasswordState(state) {
      state.resetPasswordStatus = IDLESTATUS;
      state.resetPasswordSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = LOADINGSTATUS;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = SUCCESSSTATUS;
        state.isAuthenticated = true;
        state.isLoggingOut = false;
        state.authJWTToken = action.payload.authJWTToken;
        state.expiresAt = action.payload.expiresAt;
        state.message = action.payload.message;
        state.userId = action.payload.userId;
        state.activated = action.payload.activated;
        state.name = action.payload.name;
        state.emailId = action.payload.emailId;
        state.phoneNumber = action.payload.phoneNumber;
        state.role = action.payload.role;
        state.subscribedPlan = action.payload.subscribedPlan;
        state.subscribedPlannedStartDate =
          action.payload.subscribedPlannedStartDate;
        state.subscribedPlannedEndDate =
          action.payload.subscribedPlannedEndDate;
        state.restaurantId = action.payload.restaurantId;
        state.tokenId = action.payload.tokenId;
        localStorage.setItem("authJWTToken", action.payload.authJWTToken);
        localStorage.setItem("expiresAt", action.payload.expiresAt);
        localStorage.setItem("userId", action.payload.userId);
        localStorage.setItem("tokenId", action.payload.tokenId);
        localStorage.setItem("role", action.payload.role);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = FAILEDSTATUS;
        state.error = action.payload;
      })

      .addCase(resetPasswordSendOTP.pending, (state) => {
        state.resetPasswordStatus = "sending";
      })
      .addCase(resetPasswordSendOTP.fulfilled, (state) => {
        state.resetPasswordStatus = "sent";
      })
      .addCase(resetPasswordSendOTP.rejected, (state) => {
        state.resetPasswordStatus = FAILEDSTATUS;
      })

      .addCase(resetPasswordVerifyOTPAndResetPassword.pending, (state) => {
        state.resetPasswordStatus = "verifying";
      })
      .addCase(resetPasswordVerifyOTPAndResetPassword.fulfilled, (state) => {
        state.resetPasswordStatus = SUCCESSSTATUS;
        state.resetPasswordSuccess = true;
      })
      .addCase(resetPasswordVerifyOTPAndResetPassword.rejected, (state) => {
        state.resetPasswordStatus = FAILEDSTATUS;
      })

      .addCase(restoreAuth.pending, (state) => {
        state.status = LOADINGSTATUS;
        state.error = null;
      })
      .addCase(restoreAuth.fulfilled, (state, action) => {
        state.status = SUCCESSSTATUS;
        state.isAuthenticated = true;
        state.isLoggingOut = false;
        state.authJWTToken = action.payload.authJWTToken;
        state.expiresAt = action.payload.expiresAt;
        state.message = action.payload.message;
        state.userId = action.payload.userId;
        state.activated = action.payload.activated;
        state.name = action.payload.name;
        state.emailId = action.payload.emailId;
        state.phoneNumber = action.payload.phoneNumber;
        state.role = action.payload.role;
        state.subscribedPlan = action.payload.subscribedPlan;
        state.subscribedPlannedStartDate =
          action.payload.subscribedPlannedStartDate;
        state.subscribedPlannedEndDate =
          action.payload.subscribedPlannedEndDate;
        state.restaurantId = action.payload.restaurantId;
        state.tokenId = action.payload.tokenId;
        localStorage.setItem("authJWTToken", action.payload.authJWTToken);
        localStorage.setItem("expiresAt", action.payload.expiresAt);
        localStorage.setItem("userId", action.payload.userId);
        localStorage.setItem("tokenId", action.payload.tokenId);
        localStorage.setItem("role", action.payload.role);
      })
      .addCase(restoreAuth.rejected, (state, action) => {
        state.status = FAILEDSTATUS;
        state.error = action.payload;
      });
  },
});

export const { logout, resetPasswordState } = authSlice.actions;
export default authSlice.reducer;
